import * as React from 'react';

import type { HeadProps, PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';

import { CtaBlock } from 'stile-shared/src/components/2020/blocks/CtaBlock';
import { TextBlock } from 'stile-shared/src/components/2020/blocks/TextBlock';
import { H2, P, Ul } from 'stile-shared/src/components/2020/common/Primitives';

import { PageLayout } from 'templates/2024/PageLayout';
import { SEO } from 'templates/2024/SEO';

import { ResourceAccessBlock } from 'components/common/ResourceAccessBlock';

export function Head(props: HeadProps) {
  return (
    <SEO
      title="Western Australian V9 Curriculum"
      description="The complete content change guide for teachers."
      slug={props.location.pathname}
    />
  );
}

function WACurriculumPage(props: PageProps) {
  return (
    <PageLayout {...props}>
      <ResourceAccessBlock
        title="Western Australian V9 Curriculum"
        subtitle="The complete content change guide for teachers."
        id="WA_CURRICULUM_V9"
        redirectUrl="https://stileapp.com/static/Stile_WA_The_Complete_Content_Change_2025.pdf"
        image={
          <StaticImage
            src="../../../../stile-shared/assets/images/resources/WA_curriculum_v9.jpg"
            alt=""
          />
        }
      >
        <p>
          The updated curriculum for Western Australia comes into effect in 2026 and it’s a
          significant shift for science educators.
        </p>
        <p>
          This guide gives you a clear, curriculum-aligned summary of what’s changed: content
          updates, strand restructures, and what content to adapt to stay aligned with SCSA and
          support you with creating your next great science program.
        </p>
      </ResourceAccessBlock>
      <TextBlock>
        <H2>What’s inside the guide?</H2>
        <P>Get everything you need to start preparing your teaching program for 2026 </P>
        <P>
          Whether you're reviewing your scope and sequence or realigning assessments, this guide
          makes it easier to plan with clarity. Here’s what you’ll find inside:
          <Ul>
            <li>
              Strands and practices, explained: A clear overview of the revised Science learning
              area—including updated strands, sub-strands, and inquiry practices.
            </li>
            <li>
              Content changes by year level and strand: A full breakdown of Years 7–10 content
              changes, mapped across Biological, Chemical, Physical, and Earth and Space Sciences.
            </li>
            <li>
              Side-by-side comparison table: See every major and minor change from v8.1 to the
              revised curriculum, so you can spot what’s shifted at a glance—and plan accordingly.
            </li>
          </Ul>
          BONUS: Forces Teaching Guide and Lesson Pan Preview a complete Stile Teaching Plan and
          editable Lesson Planning Guide template—ready to adapt for your school.
        </P>
      </TextBlock>
      <TextBlock variant="dark">
        <H2> Why we created this guide </H2>
        <P>
          This guide was designed to give WA science educators clarity, confidence and time
          back—without compromising on rigour.
        </P>
        <P>
          Created by Stile’s curriculum team and reviewed by science educators across the country,
          it’s your reliable starting point for teaching science in 2026. Whether you’re skimming
          for focus areas or rebuilding your scope and sequence, this guide is your fast track to
          success.
        </P>
      </TextBlock>
      <CtaBlock />
    </PageLayout>
  );
}

export default WACurriculumPage;
